'use client'

import { useRouter } from 'next/navigation'
import { Button, Icon } from '@/design-system'
import { useTranslations } from 'next-intl'
import useUserPermissions from '@/hooks/useUserPermissions'
import ErrorBoundary from './ErrorBoundary'

function AdminErrorDetails({ error }: { error: Error }) {
  const { isAdmin } = useUserPermissions()
  if (!isAdmin) return null
  return (
    <pre className="mt-4 w-full text-left text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3 overflow-auto max-h-48">
      {error.stack || error.message}
    </pre>
  )
}

interface ErrorStateProps {
  error?: Error | null
  reset?: () => void
  title?: string
  description?: string
  showHomeButton?: boolean
  showRefreshButton?: boolean
  fullScreen?: boolean
}

export default function ErrorState({
  error,
  reset,
  title,
  description,
  showHomeButton = true,
  showRefreshButton = true,
  fullScreen = false,
}: ErrorStateProps) {
  const t = useTranslations('shared.errorState')
  const router = useRouter()
  const resolvedTitle = title ?? t('defaultTitle')
  const resolvedDescription = description ?? t('defaultDescription')

  const handleRefresh = () => {
    if (reset) {
      reset()
      return
    }
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  const handleGoHome = () => {
    router.push('/dashboard')
  }

  return (
    <div
      className={
        fullScreen
          ? 'flex items-center justify-center min-h-screen p-6'
          : 'flex items-center justify-center min-h-[60vh] p-6'
      }
    >
      <div className="flex flex-col items-center text-center max-w-md">
        <div className="p-4 rounded-full bg-[var(--color-surface-hover)] mb-4">
          <Icon
            name="error"
            size="xl"
            className="text-[var(--color-pink)]"
          />
        </div>
        <h2 className="text-xl font-semibold text-foreground">{resolvedTitle}</h2>
        <p className="text-sm text-muted-foreground mt-2">{resolvedDescription}</p>

        {error && (
          <ErrorBoundary fallback={() => null}>
            <AdminErrorDetails error={error} />
          </ErrorBoundary>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {showRefreshButton && (
            <Button onClick={handleRefresh} variant="default">
              <Icon name="refresh" size="sm" className="mr-2" />
              {t('tryAgain')}
            </Button>
          )}
          {showHomeButton && (
            <Button onClick={handleGoHome} variant="secondary">
              {t('goToDashboard')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
